import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { toNumber } from '../../utils/numbers';
import { ScaleTypes } from '../discharge-store';
import convertUnit from '../../utils/unit-converter';
import { finishDischarge as _finishDischarge } from '../batch/discharge-operations/discharge-control';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import type{ IScale } from '../../../types/device/DeviceModels';
import type { DischargeScale } from '../../../types/api/Discharge';
import { beltSwitch } from './device-switches/belt-switch';
import { BatchAction } from '../../constants/MaterialModels';
import { RemoteDisplayBatchStatus } from '../../constants/RemoteDisplayBatchStatus';
import { aeratorSwitch } from './device-switches/aerator-switch';
import { screwSwitch } from './device-switches/screw-switch';
import { vibratorSwitch } from './device-switches/vibrator-switch';
import { checkIfDischargeStartPercentForPumpsReady } from './checkAdmixtureDischargeConditions';
import type { IWeighedDataToBatch } from '../../../types/batch/DataToBatch';

export const dischargePrimaryMixin = {

  finishDischarge(this: IBatchStore, runningData: IBatchData) {
    return _finishDischarge(this, runningData);
  },

  pauseDischargeScale(this: IBatchStore, scale: IScale|any, status?: string): void {

    if (this.isAbort || scale.decumulative) {
      return;
    }

    this.closeScale(scale);

    if (this.isDoubleGateScale(scale)) {
      this.closeScale(scale, scale.openGateTwoPNo, scale.closeGateTwoPNo);
    }

    this.deviceStatus.set(scale.id, {
      ...this.getDeviceStatus(scale.id),
      status: status || DeviceStatus.WAIT_WATER,
    });

    scale.dischargeIsStop = true;
  },

  resumeDischargeScale(this: IBatchStore, scale: DischargeScale) {

    if (this.isAbort || scale.decumulative) {
      return;
    }

    this.deviceStatus.set(scale.id, {
      ...this.getDeviceStatus(scale.id),
      status: DeviceStatus.WAIT_DISCH,
    });

    scale.dischargeIsStop = false;
  },

  isMoreDrops(this: IBatchStore) {

    return this.running.length > 1 || !!this.queue.length;
  },

  onDischargeFinish(this: IBatchStore, scale: IScale | any, runningData: IBatchData): void {

    if (this.isAbort) {
      this.closeScale(scale);

      if (this.isDoubleGateScale(scale)) {
        this.closeScale(scale, scale.openGateTwoPNo, scale.closeGateTwoPNo);
      }

      return;
    }

    if (!scale?.id || !runningData || !this.isActiveDrop(runningData) || runningData.finalScalesDischarged.includes(scale.id)) {
      return;
    }

    runningData.sequence.discharges = runningData.sequence.discharges.filter((v: DischargeScale) => runningData.finalScales[v.scaleId]);
    runningData.sequence.discharges
      .forEach((s: DischargeScale, i: number) => {
        runningData.sequence.discharges[i] = { ...s, ...this.devicesStore.getDeviceById(s.scaleId) };
      });

    const _scale = runningData.sequence.discharges.find((v: DischargeScale | IScale) => v.id === scale.id);

    if (_scale) {
      scale = _scale;
    } else {
      logger.warn(`no scale: ${scale.description} in discharge sequence: ${runningData.sequence.name}`);
    }

    if (scale.dischargeFinish) {
      logger.log('discharge already finish', scale.id);
      return;
    }

    const waterScale = runningData.sequence.discharges.find((dischargeScale: DischargeScale) => dischargeScale.type === ScaleTypes.WATER);
    const primary = runningData.sequence.discharges.find((dischargeScale: DischargeScale) => dischargeScale.isPrimary);
    let secondaries = runningData.sequence.discharges.filter((dischargeScale: DischargeScale) => !dischargeScale.isPrimary);
    const primaries = runningData.sequence.discharges.filter((dischargeScale: DischargeScale) => dischargeScale.isPrimary);
    const s = this.getDeviceStatus(scale.id);

    logger.log(`onDischargeFinish: #${scale.id} ${scale.type}, primary:`, primary, 'secondaries length:', secondaries.length);

    if (scale.settlingTime > 0) {
      this.deviceStatus.set(scale.id, {
        ...s,
        status: DeviceStatus.SETTLING,
      });
    }

    scale.dischargeFinish = true;

    if (primary && scale.id === primary.id) {
      primary.dischargeFinish = true;
      this.updateScaleDischarge(primary, runningData);
    }

    secondaries.forEach((s: DischargeScale, i: number) => {
      if (scale.id === s.id) {
        secondaries[i].dischargeFinish = true;
        this.updateScaleDischarge(secondaries[i], runningData);
      }
    });

    this.timers.clear(timerKey.dischargeFinish(scale.id));

    this.timers.set(timerKey.dischargeFinish(scale.id), () => {
      if (this.isAbort || !this.isActiveDrop(runningData)) {
        return;
      }

      if (scale.dischargeCloseDelay > 0) {
        this.deviceStatus.set(scale.id, {
          ...this.getDeviceStatus(scale.id),
          status: DeviceStatus.CLOSE_DELAY,
        });
      }

      this.timers.clear(timerKey.dischargeFinish(scale.id));
      this.timers.set(timerKey.dischargeFinish(scale.id), () => {
        this.closeScale(scale);

        if (this.isDoubleGateScale(scale)) {
          this.closeScale(scale, scale.openGateTwoPNo, scale.closeGateTwoPNo);
        }

        beltSwitch(this.rootStore, scale.beltId, false);

        this.deviceStatus.set(scale.id, {
          ...this.getDeviceStatus(scale.id),
          status: DeviceStatus.CLOSING,
        });

        this.onScaleClose(scale, runningData, () => {

          let nextPrimary = primaries.find((v: DischargeScale) => v.order === scale.order + 1);
          logger.log('discharge finish', scale.id, scale.type, 'isPrimary:', scale.isPrimary, scale);

          if (runningData?.mixDesign && runningData.scales[scale.id]) {
            const { material } = runningData.scales[scale.id][0];
            runningData.mixDesign.forEach((v, i) => {
              if (v.constituentItemId === material.itemId) {
                v.dischTare = this.getScaleWeight(scale);
                logger.log('discharge tare', material.code, v.dischTare);
              }
            });
          }

          if (scale.type === ScaleTypes.WATER) {
            if (primary && !primary.dischargeFinish) {
              const ps = this.getDeviceStatus(primary.scaleId);
              let weight = this.getScaleWeight(primary);

              if (!scale.isPrimary) {
                // resume primary scale with WAIT_WATER status when water scale finished
                const primaryShouldResume =
                  !primary.dischargeFinish
                  && (
                    DeviceStatus.WAIT_WATER === ps.status
                    || (primary.decumulative && !primary.dischargeIsResume && !primary.withWater)
                  );
                logger.log('primary should resume discharge after water finish?', primaryShouldResume, primary.id, primary.type, primary, ps.status);

                if (primaryShouldResume) {
                  logger.log('discharge resume primary after water finish:', primary.id, primary.type, secondaries.length);
                  this.resumeDischargeScale(primary);
                  primary.dischargeIsResume = true;
                  this.updateScaleDischarge(primary, runningData);
                  this.dischargeScale(primary, runningData, () => {
                    this.onPrimaryDischarge(primary, runningData);
                  });
                }
              }

              secondaries = runningData.sequence.discharges.filter((v: DischargeScale) => !v.isPrimary);
              secondaries.forEach((s: DischargeScale, i: number) => {
                const isPercentCorrect = ps.target - weight >= ps.target * (s.start / 100);
                logger.log('discharge percent', s.id, s.type, s.start, isPercentCorrect);

                if (isPercentCorrect && s.type !== ScaleTypes.WATER) {
                  const scaleShouldResume = !s.withWater && s.dischargeIsStop && !s.dischargeFinish && !s.dischargeIsResume;
                  logger.log(s.type, s.id, 'scaleShouldResume:', scaleShouldResume, !s.withWater, s.dischargeIsStop, !s.dischargeFinish, !s.dischargeIsResume);

                  if (scaleShouldResume) {
                    // case when water and primary scale finish, and we want to resume other scale
                    logger.log('discharge resume after water finish:', s.id, s.type);

                    this.resumeDischargeScale(s);
                    secondaries[i].dischargeIsResume = true;
                    secondaries[i].dischargeStarted = true;
                    this.updateScaleDischarge(secondaries[i], runningData);

                    this.dischargeScale(s, runningData);
                  }

                  if (!s.dischargeStarted && !s.dischargeFinish && primary.dischargeFinish) {
                    // case when water and primary scale finish, and we want to start other scale
                    let ds = this.getDeviceStatus(s.id);
                    if (ds.status === DeviceStatus.WAIT_DISCH) {// check, because scale may still be feeding
                      logger.log('discharge scale after water finish:', s.id, s.type, ps.target * (s.start / 100));
                      secondaries[i].dischargeStarted = true;
                      this.updateScaleDischarge(secondaries[i], runningData);

                      this.dischargeScale(s, runningData);
                    }
                  }
                }
              });
            } else {
              runningData.sequence.discharges
                .filter((v: DischargeScale) => !v.isPrimary)
                .forEach((s: DischargeScale) => {
                  if ((s.dischargeIsStop || !s.dischargeStarted) && !s.dischargeFinish) {
                    logger.log('discharge scale after water finish:', s.id, s.type, s);
                    this.dischargeScale(s, runningData);
                  }
                });
            }
          } else {
            if (scale.isPrimary) {
              // primary finish and water is waiting
              runningData.sequence.discharges
                .filter((v: DischargeScale) => !v.isPrimary)
                .forEach((s: DischargeScale) => {
                  if ((s.dischargeIsStop || !s.dischargeStarted) && !s.dischargeFinish) {
                    logger.log('discharge scale after primary finish:', s.id, s.type, s);
                    // check if water has finish and scale is without water
                    if (waterScale) {
                      if (!waterScale.dischargeFinish && !s.withWater) {
                        logger.log('scale without water, waiting when water finish', s);
                      } else {
                        if (s.type === ScaleTypes.WATER && nextPrimary && !runningData.finalScalesDischarged.includes(nextPrimary.id) && !nextPrimary.withWater) {
                          logger.log('wash water wait, next primary without water', s);
                        } else {
                        // check if some scale !s.withWater is still discharging if so wait
                          const scalesWithoutWater = runningData.sequence.discharges.filter((v: DischargeScale) => !v.withWater && !v.dischargeFinish);
                          if (s.type === ScaleTypes.WATER && scalesWithoutWater.length) {
                            logger.log('wash water wait, other scale without water', s, scalesWithoutWater);
                          } else {
                            this.dischargeScale(s, runningData);
                          }
                        }
                      }
                    } else {
                      this.dischargeScale(s, runningData);
                    }
                  }
                });
            } else {
              runningData.sequence.discharges
                .forEach((s: DischargeScale) => {
                  if (!s.isPrimary && (s.dischargeIsStop || !s.dischargeStarted) && !s.dischargeFinish) {
                    logger.log('discharge scale after secondary finish:', s.id, s.type, s);
                    const scalesWithoutWater = runningData.sequence.discharges.filter((v: DischargeScale) => !v.withWater && !v.dischargeFinish);
                    if (s.type === ScaleTypes.WATER && (scalesWithoutWater.length || (primary && !primary.dischargeFinish))) {
                      logger.log('wash water wait, other scale without water or primary', s, scalesWithoutWater);
                    } else {
                      this.dischargeScale(s, runningData);
                    }
                  }
                  if (
                    s.isPrimary && s.dischargeIsStop && !s.dischargeFinish && s.holdAmount && s.holdForScaleId === scale.scaleId &&
                    !s.dischargeResumedAfterStop // Primary scale has already resumed earlier – no need to do it again
                  ) {
                    logger.log('resume primary after hold', s, scale);
                    this.dischargeScale(s, runningData);
                  }
                });
            }
          }

          if (this.isAbort || !this.isActiveDrop(runningData)) {
            return;
          }

          if (scale.type === ScaleTypes.WATER) {
            let shouldStopHere = false;
            runningData = this.running.find(v => v.uid === runningData.uid && v.currentDrop === runningData.currentDrop);
            runningData?.scales?.[scale.id]?.forEach((data: IWeighedDataToBatch) => {
              const { material, overcapacityTarget, isDischargeFinishHeadWater } = data;
              if (overcapacityTarget && !isDischargeFinishHeadWater) {
                this.deviceStatus.set(scale.id, {
                  ...this.getDeviceStatus(scale.id),
                  status: DeviceStatus.WAIT_FEED,
                });
                data.isDischargeFinishHeadWater = true;
                scale.dischargeFinish = false;
                secondaries.forEach((s: DischargeScale, i: number) => {
                  if (scale.id === s.id) {
                    secondaries[i].dischargeFinish = false;
                    this.updateScaleDischarge(secondaries[i], runningData);
                  }
                });
                logger.log('feed mode resume over capacity target', material.code, scale.description, data);
                this.feedMode(data, runningData, overcapacityTarget);
                shouldStopHere = true;
              }
            });

            if (shouldStopHere) {
              return;
            }
          }

          let scaleActionType;
          if (scale.type === ScaleTypes.AGG) {
            scaleActionType = BatchAction.ScaleAggDischargeComplete;
          } else if (scale.type === ScaleTypes.CEM) {
            scaleActionType = BatchAction.ScaleCemDischargeComplete;
          } else if (scale.type === ScaleTypes.WATER) {
            scaleActionType = BatchAction.ScaleWaterDischargeComplete;
          }

          if (!runningData.finalScalesDischarged.includes(scale.id)) {
            runningData.finalScalesDischarged.push(scale.id);
          }

          this.waitingForDischarge = this.waitingForDischarge.filter(v => v.scaleId !== scale.id);
          this.trackedDischargeScales = this.trackedDischargeScales.filter(scaleId => scaleId !== scale.id);

          this.deviceStatus.set(scale.id, {
            ...this.getDeviceStatus(scale.id),
            status: DeviceStatus.FINISH,
          });

          this.setDecumScaleStatus(scale.id, {
            ...this.getDeviceStatusDefault(scale),
            status: DeviceStatus.FINISH,
          });

          if (scale.remoteDisplayId) {
            const { remoteDisplayStore } = this.rootStore;
            remoteDisplayStore.setStatus(scale.remoteDisplayId, {
              status: RemoteDisplayBatchStatus.FINISH,
              balance: '',
            });
          }

          if (this.running.length > 1 || this.queue.length) {
            const s = this.getDeviceStatus(scale.id);
            if (s.status === DeviceStatus.FINISH) {
              this.deviceStatus.set(scale.id, this.getDeviceStatusDefault(scale));
            }
            this.setDecumScaleStatus(scale.id, this.getDeviceStatusDefault(scale));
          }

          logger.log(`scale discharge complete: ${scale.type} ${scaleActionType} ${scale.description}`);
          this.onBatchAction(runningData, scaleActionType);

          if (!runningData.isWashout) {
            aeratorSwitch(this.rootStore, scale.aeratorId, false);
            screwSwitch(this.rootStore, scale.screwId, false);
            beltSwitch(this.rootStore, scale.beltId, false);
            vibratorSwitch(this.rootStore, scale.vibratorId, false);
          }

          this.timers.clear(timerKey.checkDataInScaleQueue());
          this.timers.set(timerKey.checkDataInScaleQueue(), () => this.checkDataInScaleQueue(), 1000, 'Check data in scale queue');
          this.timers.clear(timerKey.nextStep());
          this.timers.set(timerKey.nextStep(), () => this.startBatch(), 2000, 'Next step');

          if (scale.isPrimary && nextPrimary && !runningData.finalScalesDischarged.includes(nextPrimary.id)) {
            logger.log('discharge next primary:', nextPrimary.id, nextPrimary.type);
            nextPrimary = {
              ...nextPrimary,
              washStart: primary.washStart,
            };

            this.dischargeScale(nextPrimary, runningData, primary => {
              this.onPrimaryDischarge(primary, runningData);
            });
            return;
          }

          if (runningData.finalScales && Object.keys(runningData.finalScales).length !== runningData.finalScalesDischarged.length) {
            return;
          }

          checkIfDischargeStartPercentForPumpsReady(this.rootStore, runningData);

          if (!this.areAllPumpsFinished(runningData)) {
            // case when water finish first and admixtures weren't ready
            Object.keys(BatchAction).forEach(action => {
              this.onBatchAction(runningData, action);
            });
          }

          this.trackedDischargeScales = [];
          this.checkPumpsFinish(runningData);

        });

      }, (scale.dischargeCloseDelay || 0) * 1000);

    }, (scale.settlingTime || 0) * 1000);

  },

  convertWater(this: IBatchStore, runningData: IBatchData) {

    let waterScale = runningData.sequence.discharges.find((v: DischargeScale) => v.type === ScaleTypes.WATER);
    if (waterScale) {
      const batchUnit = waterScale.unit;
      waterScale = { ...waterScale, ...this.devicesStore.getDeviceById(waterScale.scaleId) };
      const deviceUnit = this.getUnitById(waterScale.unitId);
      const isDeferenceUnit = batchUnit ? batchUnit !== deviceUnit : false;
      const waterStatus = this.getDeviceStatus(waterScale.scaleId);

      // case when we want discharge all metred with head water and water scale is not active
      if (waterStatus.status === DeviceStatus.IDLE) {
        this.onBatchAction(runningData, BatchAction.StartDischargingHeadWater);
      }

      if (isDeferenceUnit) {
        if (!runningData.headWaterUnconverted) {
          runningData.headWaterUnconverted = runningData.headWater;
          runningData.headWater = toNumber(convertUnit(runningData.headWater, batchUnit, deviceUnit), 5);
        }
        if (!runningData.washWaterUnconverted) {
          runningData.washWaterUnconverted = runningData.washWater;
          runningData.washWater = toNumber(convertUnit(runningData.washWater, batchUnit, deviceUnit), 5);
        }
      }
    }
  },

  updateScaleDischarge(this: IBatchStore, scale: DischargeScale, runningData: IBatchData) {

    runningData.sequence?.discharges?.forEach((v: DischargeScale, i: number) => {
      if (v.scaleId === scale.scaleId) {
        runningData.sequence.discharges[i] = { ...v, ...scale };
      }
    });
  },

};
